import { useState, useCallback } from "react";
import { STOCK_SEARCH_API_URL, BACKEND_API_URL } from "../apiConfig";

export default function useStockDataModel() {
  const [state, setState] = useState({
    name: "Stock Market Tracker",
    symbol: "",
    price: null,
    validSymbol: false,
    data: null,
    suggestions: [],
    searchQuery: "",
    selectedStock: null,
    isLoading: false
  });

  const updateState = (updates) =>
    setState((prev) => ({ ...prev, ...updates }));

  const normalizeSymbol = (symbol) =>
    String(symbol || "").replace(/\.NS$/i, "").toUpperCase();

  const searchStocks = useCallback(async (query) => {
    // If no query or empty string, fetch all stocks
    const endpoint = query && query.trim() !== '' 
      ? `${STOCK_SEARCH_API_URL}/search?q=${encodeURIComponent(query)}`
      : `${STOCK_SEARCH_API_URL}/search`;

    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error('Search failed');
      }
      const suggestions = await response.json();
      // Only update suggestions if we're not in a selected state
      if (!state.selectedStock || query !== state.selectedStock.symbol) {
        updateState({ suggestions });
      }
    } catch (error) {
      console.error('Search error:', error);
      updateState({ suggestions: [] });
    }
  }, [state.selectedStock]); // Add selectedStock to dependencies

  const getStockPrice = useCallback(async () => {
    if (!state.symbol) return;
    updateState({ isLoading: true });

    const symbolWithSuffix = `${state.symbol}.NS`;

    try {
      const response = await fetch(`${BACKEND_API_URL}/api/upstox/prices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ symbols: [symbolWithSuffix] })
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result?.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const priceData = Array.isArray(result.data)
        ? result.data.find(p => normalizeSymbol(p.symbol) === normalizeSymbol(symbolWithSuffix))
        : null;

      const latestPrice = priceData?.price ?? null;
      const errorMessage = priceData?.error || result.error || (latestPrice == null ? `Price unavailable for ${state.symbol}` : null);

      updateState({
        price: latestPrice,
        data: result,
        isLoading: false,
        error: errorMessage,
        validSymbol: !!latestPrice
      });
    } catch (error) {
      console.error('Error fetching stock price:', error);
      updateState({
        price: null,
        data: null,
        isLoading: false,
        error: error.message,
        validSymbol: false
      });
    }
  }, [state.symbol]); // Only depends on symbol now

  return {
    state,
    updateState,
    getStockPrice,
    searchStocks
  };
}
